import React from 'react';
import { createRoot } from 'react-dom/client';
import Application from './components/app/app';
import configureStore from './redux/store';
import { injectAnalytics } from './utils/analytics';
import { CATCH_ERROR } from './utils/defaults';
import { setupFBPixel } from './utils/facebook';
import { persistStoreToStorage } from './utils/persist-store';
import { retryPromise } from './utils/promise';
import { extractExperimentParams } from './web-shared/utils/experiments';

/**
 *  KU – __webpack_public_path__ is scoped to webpack
 *  so we can't set it directly during the rails-side hydration step
 *
 *  https://github.com/rails/webpacker/issues/2269#issuecomment-548816104
 *  https://github.com/webpack/webpack/issues/443#issuecomment-72288791
 * */

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
__webpack_public_path__ = window.WEBPACK_PUBLIC_PATH || __webpack_public_path__; // eslint-disable-line

const REDUX_HYDRATE = window.REDUX_HYDRATE || {};
const mountNode = document.querySelectorAll('#mount')[0];

injectAnalytics();
setupFBPixel();

const store = configureStore({
  initialState: {
    ...REDUX_HYDRATE,
    config: {
      ...REDUX_HYDRATE.config,
      experiments: {
        ...(REDUX_HYDRATE.config && REDUX_HYDRATE.config.experiments),
        ...extractExperimentParams(window.location.search),
      },
    },
  },
});

// accessibility testing
// see config options:
// https://github.com/dequelabs/axe-core/blob/master/doc/API.md#api-name-axeconfigure
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require, import/no-extraneous-dependencies, @typescript-eslint/no-var-requires
  const axe = require('@axe-core/react');
  // eslint-disable-next-line global-require, import/no-extraneous-dependencies, @typescript-eslint/no-var-requires
  const ReactDOM = require('react-dom');
  const config = {};
  axe(React, ReactDOM, 1000, config).catch(CATCH_ERROR);
}

// KU – optionally load polyfills for certain browsers
async function loadPolyfills() {
  if (typeof window.IntersectionObserver === 'undefined') {
    await retryPromise(() => import('intersection-observer'));
  }
  if (typeof window.ResizeObserver === 'undefined') {
    const module = await retryPromise(() => import('@juggle/resize-observer'));
    window.ResizeObserver = module.ResizeObserver;
  }
  if (typeof window.queueMicrotask === 'undefined') {
    const qm = await retryPromise(() => import('queue-microtask'));
    window.queueMicrotask = qm.default;
  }
}

async function onStoreRehydrate() {
  await loadPolyfills().catch(CATCH_ERROR);
  persistStoreToStorage(store);
  createRoot(mountNode).render(<Application store={store} />);
}

onStoreRehydrate().catch(CATCH_ERROR);
